// client/src/components/admin/SuperAdminDashboard.jsx
// Platform-level admin dashboard — restricted to SE3D / On-Screen tenants

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getJson, postJson, patchJson, delJson } from '../../services/http';
import AppFooter from '../AppFooter';
import { FaHome, FaSync } from 'react-icons/fa';
import './SuperAdminDashboard.css';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'tracking', label: 'Tracking' },
  { key: 'errors', label: 'Errors' },
  { key: 'tenants', label: 'Tenants' },
  { key: 'broadcast', label: 'Messages' },
  { key: 'outreach', label: 'Outreach' },
];

export default function SuperAdminDashboard() {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [authorized, setAuthorized] = useState(null); // null = loading
  const [error, setError] = useState(null);

  // Check super-admin access on mount
  useEffect(() => {
    getJson('/api/super-admin/check-access')
      .then(() => setAuthorized(true))
      .catch(() => {
        setAuthorized(false);
        setError('You do not have access to the super admin dashboard.');
      });
  }, []);

  if (authorized === null) return <div className="sa-loading">Checking access...</div>;
  if (!authorized) return <div className="super-admin"><p style={{ color: 'red' }}>{error}</p></div>;

  return (
    <div className="super-admin">
      <h2>Platform Admin</h2>

      <div className="sa-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`sa-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab />}
      {tab === 'tracking' && <TrackingTab />}
      {tab === 'errors' && <ErrorsTab />}
      {tab === 'tenants' && <TenantsTab />}
      {tab === 'broadcast' && <BroadcastTab />}
      {tab === 'outreach' && <OutreachTab />}

      <AppFooter
        buttons={[
          { icon: <FaHome />, label: 'Home', onClick: () => navigate(`/${tenant}/dashboard`) },
        ]}
      />
    </div>
  );
}

// ============================================================================
// Overview Tab — platform metrics
// ============================================================================
function OverviewTab() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    getJson('/api/super-admin/metrics')
      .then(d => setMetrics(d.metrics))
      .catch(err => console.error('Metrics load error:', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="sa-loading">Loading metrics...</div>;
  if (!metrics) return <p>Failed to load metrics.</p>;

  const maxSignup = Math.max(...metrics.signupTrend.map(s => s.count), 1);

  return (
    <div>
      <div className="sa-section-header">
        <h3>Platform Metrics</h3>
        <button className="sa-btn" onClick={load}><FaSync /> Refresh</button>
      </div>

      <div className="sa-metrics-grid">
        <MetricCard value={metrics.totalTenants} label="Total Tenants" />
        <MetricCard value={metrics.totalUsers} label="Total Users" />
        <MetricCard value={metrics.totalEvents} label="Total Events" />
        <MetricCard value={metrics.totalPosts} label="Total Posts" />
        <MetricCard value={metrics.totalClicks} label="Total Clicks" />
        <MetricCard value={metrics.newTenantsToday} label="New Tenants Today" />
        <MetricCard value={metrics.newUsersToday} label="New Users Today" />
      </div>

      <h3 style={{ fontSize: '1rem', marginBottom: 4 }}>Activation Funnel</h3>
      <div className="sa-metrics-grid">
        <MetricCard value={metrics.totalTenants} label="Account Created" />
        <MetricCard value={metrics.tenantsWithEvents} label="First Event Created" />
        <MetricCard value={metrics.tenantsWithPosts} label="First Post Scheduled" />
      </div>

      <h3 style={{ fontSize: '1rem', margin: '16px 0 4px' }}>Tenant Signups (30 days)</h3>
      {metrics.signupTrend.length > 0 ? (
        <div className="sa-bar-chart">
          {metrics.signupTrend.map((s, i) => (
            <div
              key={i}
              className="sa-bar"
              style={{ height: `${(s.count / maxSignup) * 100}%` }}
              title={`${s.date}: ${s.count}`}
            />
          ))}
        </div>
      ) : (
        <p style={{ color: '#888', fontSize: '0.85rem' }}>No signups in the last 30 days.</p>
      )}
    </div>
  );
}

function MetricCard({ value, label }) {
  return (
    <div className="sa-metric-card">
      <div className="value">{typeof value === 'number' ? value.toLocaleString() : value}</div>
      <div className="label">{label}</div>
    </div>
  );
}

// ============================================================================
// Tracking Tab — click tracking stats
// ============================================================================
function TrackingTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    getJson(`/api/super-admin/tracking?days=${days}`)
      .then(d => setData(d))
      .catch(err => console.error('Tracking load error:', err))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="sa-loading">Loading tracking data...</div>;
  if (!data) return <p>Failed to load tracking data.</p>;

  const maxDaily = Math.max(...data.daily.map(d => d.clicks), 1);

  return (
    <div>
      <div className="sa-section-header">
        <h3>Click Tracking</h3>
        <div className="sa-filters">
          <select value={days} onChange={e => setDays(Number(e.target.value))}>
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
          <button className="sa-btn" onClick={load}><FaSync /></button>
        </div>
      </div>

      <div className="sa-metrics-grid">
        <MetricCard value={data.totals.totalClicks} label="Total Clicks" />
        <MetricCard value={data.totals.tenantsWithClicks} label="Tenants with Clicks" />
        <MetricCard value={data.totals.uniqueLinks} label="Unique Links" />
      </div>

      <h3 style={{ fontSize: '1rem', margin: '12px 0 4px' }}>Daily Clicks</h3>
      {data.daily.length > 0 ? (
        <div className="sa-bar-chart">
          {data.daily.map((d, i) => (
            <div
              key={i}
              className="sa-bar"
              style={{ height: `${(d.clicks / maxDaily) * 100}%` }}
              title={`${d.date}: ${d.clicks} clicks`}
            />
          ))}
        </div>
      ) : (
        <p style={{ color: '#888', fontSize: '0.85rem' }}>No clicks in this period.</p>
      )}

      <h3 style={{ fontSize: '1rem', margin: '16px 0 8px' }}>Clicks by Tenant</h3>
      <div className="sa-table-wrap">
        <table className="sa-table">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Clicks</th>
            </tr>
          </thead>
          <tbody>
            {data.byTenant.map((r, i) => (
              <tr key={i}>
                <td>{r.companyName || `Tenant #${r.tenantId}`}</td>
                <td>{r.clicks.toLocaleString()}</td>
              </tr>
            ))}
            {data.byTenant.length === 0 && (
              <tr><td colSpan={2} style={{ textAlign: 'center', color: '#888' }}>No data</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <h3 style={{ fontSize: '1rem', margin: '16px 0 8px' }}>Top Links</h3>
      <div className="sa-table-wrap">
        <table className="sa-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Destination</th>
              <th>Clicks</th>
            </tr>
          </thead>
          <tbody>
            {data.topLinks.map((l, i) => (
              <tr key={i}>
                <td><code>{l.trackingCode}</code></td>
                <td className="sa-truncate" title={l.destination}>{l.destination}</td>
                <td>{l.clicks.toLocaleString()}</td>
              </tr>
            ))}
            {data.topLinks.length === 0 && (
              <tr><td colSpan={3} style={{ textAlign: 'center', color: '#888' }}>No data</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <h3 style={{ fontSize: '1rem', margin: '16px 0 4px' }}>Site Visits</h3>
      <div className="sa-metrics-grid">
        <MetricCard value={data.siteVisits.totals.totalVisits} label="Site Visits" />
        <MetricCard value={data.siteVisits.totals.uniquePages} label="Pages Tracked" />
      </div>

      <div className="sa-table-wrap">
        <table className="sa-table">
          <thead>
            <tr>
              <th>Page</th>
              <th>Visits</th>
            </tr>
          </thead>
          <tbody>
            {data.siteVisits.byPath.map((r, i) => (
              <tr key={i}>
                <td><code>{r.path}</code></td>
                <td>{r.visits.toLocaleString()}</td>
              </tr>
            ))}
            {data.siteVisits.byPath.length === 0 && (
              <tr><td colSpan={2} style={{ textAlign: 'center', color: '#888' }}>No visits in this period</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Errors Tab — distinct errors grouped by type+message, with counts & bulk resolve
// ============================================================================
function ErrorsTab() {
  const [errors, setErrors] = useState([]);
  const [counts, setCounts] = useState({ total: 0, active: 0, resolved: 0 });
  const [statusFilter, setStatusFilter] = useState('active');
  const [expandedKey, setExpandedKey] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const qs = statusFilter ? `?status=${statusFilter}` : '';
    getJson(`/api/super-admin/errors${qs}`)
      .then(d => {
        setErrors(d.errors || []);
        setCounts(d.counts || { total: 0, active: 0, resolved: 0 });
        setSelected(new Set());
      })
      .catch(err => console.error('Errors load error:', err))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Unique key for each grouped error row
  const errKey = (e) => `${e.type}::${e.message}`;

  const toggleSelect = (e) => {
    setSelected(prev => {
      const next = new Set(prev);
      const key = errKey(e);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === errors.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(errors.map(errKey)));
    }
  };

  const bulkResolve = async () => {
    const ids = errors
      .filter(e => selected.has(errKey(e)))
      .flatMap(e => e.ids);
    if (ids.length === 0) return;
    try {
      await postJson('/api/super-admin/errors/bulk-status', { ids, status: 'resolved' });
      load();
    } catch (err) {
      console.error('Bulk resolve error:', err);
    }
  };

  const bulkReopen = async () => {
    const ids = errors
      .filter(e => selected.has(errKey(e)))
      .flatMap(e => e.ids);
    if (ids.length === 0) return;
    try {
      await postJson('/api/super-admin/errors/bulk-status', { ids, status: 'active' });
      load();
    } catch (err) {
      console.error('Bulk reopen error:', err);
    }
  };

  return (
    <div>
      <div className="sa-section-header">
        <h3>Error Log</h3>
        <button className="sa-btn" onClick={load}><FaSync /> Refresh</button>
      </div>

      <div className="sa-metrics-grid" style={{ marginBottom: 12 }}>
        <MetricCard value={counts.active} label="Active" />
        <MetricCard value={counts.resolved} label="Resolved" />
        <MetricCard value={counts.total} label="Total" />
      </div>

      <div className="sa-filters" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All</option>
          <option value="active">Active</option>
          <option value="resolved">Resolved</option>
        </select>
        {errors.length > 0 && (
          <>
            <button className="sa-btn" onClick={selectAll} style={{ marginLeft: 8 }}>
              {selected.size === errors.length ? 'Deselect All' : 'Select All'}
            </button>
            {selected.size > 0 && (
              <>
                <button className="sa-btn success" onClick={bulkResolve}>
                  Resolve Selected ({selected.size})
                </button>
                <button className="sa-btn danger" onClick={bulkReopen}>
                  Reopen Selected ({selected.size})
                </button>
              </>
            )}
          </>
        )}
      </div>

      {loading ? (
        <div className="sa-loading">Loading errors...</div>
      ) : errors.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#888', padding: 20 }}>No errors found</div>
      ) : (
        <div className="sa-card-list">
          {errors.map(e => {
            const key = errKey(e);
            const isSelected = selected.has(key);
            const isExpanded = expandedKey === key;
            return (
              <div
                key={key}
                className={`sa-card${isSelected ? ' sa-card-selected' : ''}`}
                onClick={() => setExpandedKey(isExpanded ? null : key)}
              >
                <div className="sa-card-row" style={{ alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(ev) => { ev.stopPropagation(); toggleSelect(e); }}
                    onClick={(ev) => ev.stopPropagation()}
                    style={{ marginRight: 8, width: 18, height: 18 }}
                  />
                  <span className="sa-card-label">Type</span>
                  <code>{e.type}</code>
                  <span className="sa-error-count-badge">{e.errorCount}</span>
                </div>
                <div className="sa-card-row">
                  <span className="sa-card-label">Message</span>
                  <span className="sa-card-value-wrap">{e.message || '—'}</span>
                </div>
                <div className="sa-card-row">
                  <span className="sa-card-label">Latest</span>
                  <span>{e.latestAt ? new Date(e.latestAt).toLocaleString() : '—'}</span>
                </div>
                <div className="sa-card-row">
                  <span className="sa-card-label">Tenant</span>
                  <span>{e.tenantName || (e.tenantId ? `#${e.tenantId}` : '—')}</span>
                </div>
                <div className="sa-card-row">
                  <span className="sa-card-label">Status</span>
                  <span className={`sa-badge ${e.status}`}>{e.status}</span>
                </div>
                {isExpanded && (
                  <div className="sa-error-detail">
                    <strong>Occurrences:</strong> {e.errorCount}{'\n'}
                    <strong>First seen:</strong> {e.earliestAt ? new Date(e.earliestAt).toLocaleString() : '—'}{'\n'}
                    <strong>Last seen:</strong> {e.latestAt ? new Date(e.latestAt).toLocaleString() : '—'}{'\n'}
                    <strong>URL:</strong> {e.url || '—'}{'\n'}
                    <strong>User Agent:</strong> {e.userAgent || '—'}{'\n'}
                    <strong>Platform:</strong> {e.platform || '—'}{'\n\n'}
                    <strong>Stack Trace:</strong>{'\n'}{e.stack || 'No stack trace'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Tenants Tab — list, enable/disable
// ============================================================================
function TenantsTab() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    getJson('/api/super-admin/tenants')
      .then(d => setTenants(d.tenants || []))
      .catch(err => console.error('Tenants load error:', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleTenant = async (id, currentActive) => {
    if (!window.confirm(`${currentActive ? 'Disable' : 'Enable'} this tenant?`)) return;
    try {
      await patchJson(`/api/super-admin/tenants/${id}/status`, { active: !currentActive });
      load();
    } catch (err) {
      console.error('Tenant status toggle error:', err);
    }
  };

  const toggleBilling = async (id, currentEnabled) => {
    const verb = currentEnabled ? 'Disable' : 'Enable';
    if (!window.confirm(`${verb} billing enforcement for this tenant? When disabled, all post/integration/user limits are skipped for testing.`)) return;
    try {
      await patchJson(`/api/super-admin/tenants/${id}/billing-enabled`, { enabled: !currentEnabled });
      load();
    } catch (err) {
      console.error('Tenant billing toggle error:', err);
    }
  };

  const filtered = tenants.filter(t =>
    !search || t.companyName.toLowerCase().includes(search.toLowerCase()) ||
    t.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="sa-section-header">
        <h3>Tenants ({tenants.length})</h3>
        <button className="sa-btn" onClick={load}><FaSync /> Refresh</button>
      </div>

      <div className="sa-filters">
        <input
          type="text"
          placeholder="Search tenants..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="sa-loading">Loading tenants...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#888', padding: 20 }}>No tenants found</div>
      ) : (
        <div className="sa-card-list">
          {filtered.map(t => (
            <div key={t.id} className="sa-card">
              <div className="sa-card-row">
                <span className="sa-card-label">ID</span>
                <span>{t.id}</span>
              </div>
              <div className="sa-card-row">
                <span className="sa-card-label">Company</span>
                <span>{t.companyName}</span>
              </div>
              <div className="sa-card-row">
                <span className="sa-card-label">Email</span>
                <span className="sa-card-value-wrap">{t.email}</span>
              </div>
              <div className="sa-card-row sa-card-row-stats">
                <span><strong>{t.userCount}</strong> Users</span>
                <span><strong>{t.eventCount}</strong> Events</span>
                <span><strong>{t.postCount}</strong> Posts</span>
              </div>
              <div className="sa-card-row">
                <span className="sa-card-label">Status</span>
                <span className={`sa-badge ${t.active ? 'enabled' : 'disabled'}`}>
                  {t.active ? 'Active' : 'Disabled'}
                </span>
              </div>
              <div className="sa-card-row">
                <span className="sa-card-label">Billing</span>
                <span className={`sa-badge ${t.billingEnabled ? 'enabled' : 'disabled'}`}>
                  {t.billingEnabled ? 'Enforced' : 'Unrestricted'}
                </span>
              </div>
              <div className="sa-card-row">
                <span className="sa-card-label">Created</span>
                <span>{t.createdOn ? new Date(t.createdOn).toLocaleDateString() : '—'}</span>
              </div>
              <div className="sa-card-actions">
                <button
                  className={`sa-btn ${t.active ? 'danger' : 'success'}`}
                  onClick={() => toggleTenant(t.id, t.active)}
                >
                  {t.active ? 'Disable' : 'Enable'}
                </button>
                <button
                  className={`sa-btn ${t.billingEnabled ? 'success' : 'danger'}`}
                  onClick={() => toggleBilling(t.id, t.billingEnabled)}
                  title="Toggle billing/limit enforcement for this tenant"
                >
                  {t.billingEnabled ? 'Unrestrict' : 'Enforce Billing'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Broadcast Tab — send messages to tenants
// ============================================================================
function BroadcastTab() {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [targetType, setTargetType] = useState('all');
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    getJson('/api/super-admin/tenants').then(d => setTenants(d.tenants || [])).catch(() => {});
    getJson('/api/super-admin/broadcasts').then(d => setBroadcasts(d.broadcasts || [])).catch(() => {});
  }, []);

  const toggleTenantSelect = (id) => {
    setSelectedTenants(prev =>
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      setMessage({ type: 'error', text: 'Subject and message body are required.' });
      return;
    }
    if (targetType === 'specific' && selectedTenants.length === 0) {
      setMessage({ type: 'error', text: 'Select at least one tenant.' });
      return;
    }

    if (!window.confirm(`Send this message to ${targetType === 'all' ? 'ALL tenants' : `${selectedTenants.length} tenant(s)`}?`)) {
      return;
    }

    setSending(true);
    setMessage(null);
    try {
      const result = await postJson('/api/super-admin/broadcast', {
        subject: subject.trim(),
        body: body.trim(),
        targetType,
        targetTenantIds: targetType === 'specific' ? selectedTenants : undefined,
      });
      setMessage({
        type: 'success',
        text: `Message sent to ${result.recipientCount} tenant(s).`,
      });
      setSubject('');
      setBody('');
      setSelectedTenants([]);
      // Refresh broadcasts list
      getJson('/api/super-admin/broadcasts').then(d => setBroadcasts(d.broadcasts || [])).catch(() => {});
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to send message.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <h3 style={{ fontSize: '1.1rem', marginBottom: 12 }}>Send Message</h3>

      {message && (
        <div style={{
          padding: '8px 12px',
          marginBottom: 12,
          borderRadius: 4,
          background: message.type === 'error' ? '#fff0f0' : '#f0fff0',
          color: message.type === 'error' ? '#c00' : '#070',
          fontSize: '0.85rem',
        }}>
          {message.text}
        </div>
      )}

      <div className="sa-broadcast-form">
        <div className="form-group">
          <label>Target</label>
          <select value={targetType} onChange={e => setTargetType(e.target.value)}>
            <option value="all">All Tenants</option>
            <option value="specific">Specific Tenants</option>
          </select>
        </div>

        {targetType === 'specific' && (
          <div className="form-group">
            <label>Select Tenants ({selectedTenants.length} selected)</label>
            <div className="sa-tenant-select">
              {tenants.map(t => (
                <label key={t.id}>
                  <input
                    type="checkbox"
                    checked={selectedTenants.includes(t.id)}
                    onChange={() => toggleTenantSelect(t.id)}
                  />
                  {t.companyName} ({t.email})
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="form-group">
          <label>Subject</label>
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Message subject..."
          />
        </div>

        <div className="form-group">
          <label>Message</label>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Write your message..."
          />
        </div>

        <button
          className="sa-btn primary"
          onClick={handleSend}
          disabled={sending}
        >
          {sending ? 'Sending...' : 'Send Message'}
        </button>
      </div>

      <h3 style={{ fontSize: '1.1rem', margin: '20px 0 12px' }}>Sent Messages</h3>
      {broadcasts.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#888', padding: 20 }}>No messages sent yet</div>
      ) : (
        <div className="sa-card-list">
          {broadcasts.map(b => (
            <div key={b.id} className="sa-card" style={{ cursor: 'default' }}>
              <div className="sa-card-row">
                <span className="sa-card-label">Date</span>
                <span>{new Date(b.createdAt).toLocaleString()}</span>
              </div>
              <div className="sa-card-row">
                <span className="sa-card-label">Target</span>
                <span>{b.targetType === 'all' ? 'All' : `${(b.targetTenantIds || []).length} tenant(s)`}</span>
              </div>
              <div className="sa-card-row">
                <span className="sa-card-label">Subject</span>
                <span>{b.subject}</span>
              </div>
              <div style={{ fontSize: '0.82rem', color: '#555', marginTop: 4, wordBreak: 'break-word' }}>
                {b.body}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Outreach — cold-email campaigns to prospective customers.
// Backend: server/routes/outreachRoutes.js (/api/super-admin/outreach)
// ============================================================================

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const OUTREACH_BASE = '/api/super-admin/outreach';

// Parse pasted / uploaded prospect rows: "email, First Name, Organization".
// Tolerates a header row, quoted fields, tabs or commas, and blank lines.
function parseRecipients(text) {
  const rows = [];
  const seen = new Set();
  let invalid = 0;
  let duplicate = 0;

  String(text || '').split(/\r?\n/).forEach((line, idx) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    const cells = trimmed
      .split(/\t|,/)
      .map(c => c.trim().replace(/^["']|["']$/g, '').trim());
    const email = (cells[0] || '').toLowerCase();
    // Skip an obvious header row.
    if (idx === 0 && !EMAIL_RE.test(email)) return;
    if (!EMAIL_RE.test(email)) { invalid += 1; return; }
    if (seen.has(email)) { duplicate += 1; return; }
    seen.add(email);
    rows.push({
      email,
      firstName: cells[1] || '',
      organizationName: cells[2] || '',
    });
  });

  return { rows, invalid, duplicate };
}

function OutreachTab() {
  const [view, setView] = useState('list'); // 'list' | 'new' | 'detail'
  const [detailId, setDetailId] = useState(null);

  return (
    <div>
      {view === 'list' && (
        <OutreachList
          onNew={() => setView('new')}
          onOpen={(id) => { setDetailId(id); setView('detail'); }}
        />
      )}
      {view === 'new' && (
        <OutreachNew
          onCancel={() => setView('list')}
          onCreated={(id) => { setDetailId(id); setView('detail'); }}
        />
      )}
      {view === 'detail' && detailId && (
        <OutreachDetail id={detailId} onBack={() => setView('list')} />
      )}
    </div>
  );
}

function OutreachList({ onNew, onOpen }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSuppression, setShowSuppression] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getJson(`${OUTREACH_BASE}/campaigns`)
      .then(d => setCampaigns(d.campaigns || []))
      .catch(() => setCampaigns([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="sa-section-header">
        <h3>Outreach Campaigns</h3>
        <button className="sa-btn primary" onClick={onNew}>New Campaign</button>
      </div>

      {loading ? (
        <div className="sa-loading">Loading…</div>
      ) : campaigns.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#888', padding: 20 }}>No campaigns yet.</div>
      ) : (
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr>
                <th>Name</th><th>Status</th><th>Recipients</th>
                <th>Sent</th><th>Opened</th><th>Clicked</th><th>Unsub</th><th>Created</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map(c => (
                <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => onOpen(c.id)}>
                  <td>{c.name}</td>
                  <td><span className={`sa-badge ${c.status === 'sent' ? 'resolved' : c.status === 'sending' ? 'active' : ''}`}>{c.status}</span></td>
                  <td>{c.stats.recipients}</td>
                  <td>{c.stats.sent}</td>
                  <td>{c.stats.opened}</td>
                  <td>{c.stats.clicked}</td>
                  <td>{c.stats.unsubscribed}</td>
                  <td>{new Date(c.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 24 }}>
        <button className="sa-btn" onClick={() => setShowSuppression(v => !v)}>
          {showSuppression ? 'Hide' : 'Show'} suppression list
        </button>
        {showSuppression && <SuppressionManager />}
      </div>
    </div>
  );
}

function SuppressionManager() {
  const [entries, setEntries] = useState([]);
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    getJson(`${OUTREACH_BASE}/suppression`).then(d => setEntries(d.entries || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setMsg(null);
    try {
      await postJson(`${OUTREACH_BASE}/suppression`, { email: email.trim() });
      setEmail('');
      load();
    } catch (err) {
      setMsg(err.message || 'Failed to add');
    }
  };
  const remove = async (e) => {
    try { await delJson(`${OUTREACH_BASE}/suppression/${encodeURIComponent(e)}`); load(); } catch { /* noop */ }
  };

  return (
    <div className="sa-broadcast-form" style={{ marginTop: 12 }}>
      <div className="form-group">
        <label>Do-not-contact list ({entries.length})</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="text" value={email} placeholder="email@example.com"
            onChange={e => setEmail(e.target.value)} />
          <button className="sa-btn" onClick={add} disabled={!EMAIL_RE.test(email.trim())}>Add</button>
        </div>
        {msg && <div style={{ color: '#c00', fontSize: '0.8rem', marginTop: 4 }}>{msg}</div>}
      </div>
      {entries.length > 0 && (
        <div className="sa-table-wrap" style={{ maxHeight: 260, overflow: 'auto' }}>
          <table className="sa-table">
            <thead><tr><th>Email</th><th>Reason</th><th>Added</th><th /></tr></thead>
            <tbody>
              {entries.map(en => (
                <tr key={en.email}>
                  <td>{en.email}</td>
                  <td>{en.reason}</td>
                  <td>{new Date(en.created_at).toLocaleDateString()}</td>
                  <td><button className="sa-btn danger" onClick={() => remove(en.email)}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OutreachNew({ onCancel, onCreated }) {
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [fromName, setFromName] = useState('The On-Screen Team');
  const [ctaUrl, setCtaUrl] = useState('https://on-screen.app/about');
  const [previewText, setPreviewText] = useState('');
  const [raw, setRaw] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const parsed = parseRecipients(raw);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setRaw(prev => (prev ? prev + '\n' : '') + String(reader.result || ''));
    reader.readAsText(file);
    e.target.value = '';
  };

  const doPreview = async () => {
    setMsg(null);
    try {
      const d = await postJson(`${OUTREACH_BASE}/preview`, {
        fromName, previewText, subject,
        sample: parsed.rows[0] || undefined,
      });
      setPreviewHtml(d.html || '');
    } catch (err) {
      setMsg(err.message || 'Preview failed');
    }
  };

  const doCreate = async () => {
    setMsg(null);
    if (!name.trim() || !subject.trim()) { setMsg('Name and subject are required.'); return; }
    if (parsed.rows.length === 0) { setMsg('Add at least one valid recipient.'); return; }
    setBusy(true);
    try {
      const d = await postJson(`${OUTREACH_BASE}/campaigns`, {
        name: name.trim(), subject: subject.trim(), fromName, ctaUrl, previewText,
        recipients: parsed.rows,
      });
      onCreated(d.campaignId);
    } catch (err) {
      setMsg(err.response?.data?.error || err.message || 'Failed to create campaign');
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="sa-section-header">
        <h3>New Campaign</h3>
        <button className="sa-btn" onClick={onCancel}>Cancel</button>
      </div>

      {msg && <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 4, background: '#fff0f0', color: '#c00', fontSize: '0.85rem' }}>{msg}</div>}

      <div className="sa-broadcast-form">
        <div className="form-group">
          <label>Campaign name (internal)</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Skate shops — March" />
        </div>
        <div className="form-group">
          <label>Subject line</label>
          <input type="text" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Leave blank to auto-generate per recipient" />
        </div>
        <div className="form-group">
          <label>From</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="text" value={fromName} onChange={e => setFromName(e.target.value)} placeholder="Sender name" />
            <input type="text" value="info@on-screen.app" readOnly style={{ background: '#f3f4f6' }} />
          </div>
        </div>
        <div className="form-group">
          <label>CTA link</label>
          <input type="text" value={ctaUrl} onChange={e => setCtaUrl(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Inbox preview text (optional)</label>
          <input type="text" value={previewText} onChange={e => setPreviewText(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Recipients — one per line: <code>email, First Name, Organization</code></label>
          <textarea value={raw} onChange={e => setRaw(e.target.value)} rows={8}
            placeholder={'jane@shop.com, Jane, Riverside Skate\njohn@club.org, John, North End Booster Club'} />
          <div style={{ marginTop: 6, fontSize: '0.82rem', color: '#555' }}>
            <input type="file" accept=".csv,.txt" onChange={onFile} />
            <span style={{ marginLeft: 12 }}>
              <strong>{parsed.rows.length}</strong> valid
              {parsed.invalid > 0 && <> · <span style={{ color: '#c00' }}>{parsed.invalid} invalid</span></>}
              {parsed.duplicate > 0 && <> · {parsed.duplicate} duplicate</>}
              {' '}(suppressed addresses are removed on save)
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="sa-btn" onClick={doPreview}>Preview email</button>
          <button className="sa-btn primary" onClick={doCreate} disabled={busy}>
            {busy ? 'Creating…' : 'Create campaign'}
          </button>
        </div>
      </div>

      {previewHtml && (
        <div style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: '1rem', marginBottom: 8 }}>Preview</h3>
          <iframe title="Email preview" srcDoc={previewHtml}
            style={{ width: '100%', height: 640, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff' }} />
        </div>
      )}
    </div>
  );
}

function OutreachDetail({ id, onBack }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    getJson(`${OUTREACH_BASE}/campaigns/${id}`).then(setData).catch(() => setData(null));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Poll while a send is in progress.
  useEffect(() => {
    if (data?.campaign?.status !== 'sending') return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [data?.campaign?.status, load]);

  if (!data) return <div className="sa-loading">Loading…</div>;

  const { campaign, stats, recipients } = data;

  const send = async () => {
    if (!window.confirm(`Send this campaign from info@on-screen.app?`)) return;
    setBusy(true); setMsg(null);
    try {
      await postJson(`${OUTREACH_BASE}/campaigns/${id}/send`, {});
      load();
    } catch (err) {
      setMsg(err.response?.data?.error || err.message || 'Failed to send');
    } finally {
      setBusy(false);
    }
  };
  const pause = async () => {
    setBusy(true);
    try { await postJson(`${OUTREACH_BASE}/campaigns/${id}/pause`, {}); load(); }
    catch { /* noop */ }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="sa-section-header">
        <h3>{campaign.name}</h3>
        <button className="sa-btn" onClick={onBack}>Back</button>
      </div>

      {msg && <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 4, background: '#fff0f0', color: '#c00', fontSize: '0.85rem' }}>{msg}</div>}

      <div className="sa-metrics-grid" style={{ marginBottom: 16 }}>
        {[
          ['Recipients', stats.recipients],
          ['Sent', stats.sent],
          ['Opened', stats.opened],
          ['Clicked', stats.clicked],
          ['Unsubscribed', stats.unsubscribed],
          ['Failed', stats.failed],
        ].map(([label, value]) => (
          <div className="sa-metric-card" key={label}>
            <div className="value">{value}</div>
            <div className="label">{label}</div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 12, fontSize: '0.85rem', color: '#555' }}>
        Status <span className={`sa-badge ${campaign.status === 'sent' ? 'resolved' : campaign.status === 'sending' ? 'active' : ''}`}>{campaign.status}</span>
        {' · '}Subject: {campaign.subject}
        {' · '}CTA: {campaign.ctaUrl}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['draft', 'paused'].includes(campaign.status) && (
          <button className="sa-btn primary" onClick={send} disabled={busy}>
            {campaign.status === 'paused' ? 'Resume sending' : 'Send campaign'}
          </button>
        )}
        {campaign.status === 'sending' && (
          <button className="sa-btn" onClick={pause} disabled={busy}>Pause</button>
        )}
      </div>

      <div className="sa-table-wrap" style={{ maxHeight: 460, overflow: 'auto' }}>
        <table className="sa-table">
          <thead>
            <tr><th>Email</th><th>Name</th><th>Org</th><th>Status</th><th>Opens</th><th>Clicks</th></tr>
          </thead>
          <tbody>
            {recipients.map(r => (
              <tr key={r.id}>
                <td>{r.email}</td>
                <td>{r.firstName || '—'}</td>
                <td>{r.organizationName || '—'}</td>
                <td>
                  <span className={`sa-badge ${r.status === 'sent' ? 'resolved' : r.status === 'failed' ? 'disabled' : ''}`}>{r.status}</span>
                  {r.error && <span title={r.error} style={{ color: '#c00', marginLeft: 4 }}>⚠</span>}
                </td>
                <td>{r.openCount}</td>
                <td>{r.clickCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
